import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { MAX_MESSAGE_LENGTH, type FileAsset, type MessageAttachment } from '@itsappy/shared';
import { api } from '@/lib/api';
import { toast } from '@/store/ui';
import { Spinner } from '@/components/ui';
import {
  AttachIcon,
  CameraIcon,
  CloseIcon,
  EmojiIcon,
  MicIcon,
  PlusIcon,
  SendIcon,
  TrashIcon,
} from '@/components/ui/icons';
import { fileIconFor } from '@/components/ui/fileIcons';
import { cn, formatBytes } from '@/lib/utils';
import { useFileDrop } from '@/hooks/useFileDrop';
import { AttachmentPreview } from './AttachmentPreview';
import { EmojiPicker } from './EmojiPicker';
import { WalkieButton } from './WalkieButton';
import { GifPicker } from './GifPicker';
import { FormatToolbar } from './FormatToolbar';
import { ScheduleSendMenu } from './ScheduleSendMenu';
import { speechRecognitionAvailable, startSpeechCapture, type SpeechCapture } from '@/lib/speech';
import { dictationLanguage, dictationLocale, setDictationLanguage } from '@/lib/dictationLanguage';
import { useAssistantStatus } from '@/hooks/assistant';
import {
  insertLink,
  toggleLinePrefix,
  toggleWrap,
  wrapCodeFence,
  type TextEdit,
} from '@/lib/composerText';

const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** The server caps a single upload at 10 files, so the composer does too. */
const ATTACHMENT_LIMIT = 10;

/** How many peaks a recording stores. Enough to read as a voice, small
 *  enough to sit inside the message document without bloating it. */
const WAVEFORM_BUCKETS = 60;

/** How often the level meter samples while recording. */
const LEVEL_INTERVAL = 80;

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  /**
   * Extra attachments — a finished voice note, a GIF — go out with this
   * message. A voice note carries its waveform and, where the browser managed
   * one, its transcript, so no follow-up request is needed to attach either.
   *
   * `options.body` replaces the draft as the message body. The caption typed
   * over a file preview lives in that dialog, not in the composer, so it has
   * to travel with the send rather than be read off state that never held it.
   */
  onSubmit: (extraAttachments?: MessageAttachment[], options?: { body?: string }) => void;
  attachments: MessageAttachment[];
  onAttachmentsChange: (attachments: MessageAttachment[]) => void;
  placeholder: string;
  /** Blocks input while the previous message is still in flight. */
  disabled?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement>;
  /** Opens the poll dialog; omitted where polls do not belong. */
  onOpenPoll?: () => void;
  /** Sends the current draft at a chosen time; omitted where unsupported. */
  onSchedule?: (sendAt: Date) => void;
  /**
   * First refusal on a keypress. Returning true means the surrounding chat
   * handled it — arrowing through mention suggestions, ↑ to edit the last
   * message — and the composer should not also act on it.
   */
  interceptKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  /** Hides the attach/GIF/camera controls, for a body-only edit. */
  hideAttachments?: boolean;
  /**
   * Where a dragged file may be dropped to attach it. Point this at the whole
   * conversation pane — people aim at the messages they are replying to, not
   * at the input — and the highlight is drawn over that element. Without it
   * the composer itself is the target.
   */
  dropZoneRef?: RefObject<HTMLElement>;
  /**
   * Puts the walkie talkie beside the microphone, for the channel named.
   * Omitted where a live radio makes no sense — editing a message, or a
   * thread, whose readers are not the channel's.
   */
  walkie?: { channelId: string; channelName: string };
  /** Anchored above the composer: mention suggestions, thread banner. */
  children?: ReactNode;
  className?: string;
}

/**
 * MediaRecorder defaults to WebM/Opus in Chromium, which iOS cannot decode —
 * voice notes recorded here were unplayable on iPhone. Prefer an MP4/AAC
 * container wherever the browser can produce one, and fall back only when it
 * genuinely cannot.
 */
function preferredRecordingType(): MediaRecorderOptions | undefined {
  const candidates = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac'];
  for (const mimeType of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType)) {
      return { mimeType };
    }
  }
  return undefined;
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4') || mimeType.includes('aac')) return 'm4a';
  if (mimeType.includes('mpeg')) return 'mp3';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

/** Normalises captured levels to 0..1 and folds them into fixed buckets. */
function toWaveform(levels: number[]): number[] {
  if (!levels.length) return [];
  const peak = Math.max(...levels);
  if (peak <= 0) return [];
  const size = levels.length / WAVEFORM_BUCKETS;
  return Array.from({ length: WAVEFORM_BUCKETS }, (_, i) => {
    const start = Math.floor(i * size);
    const end = Math.max(start + 1, Math.floor((i + 1) * size));
    let max = 0;
    for (let j = start; j < end && j < levels.length; j += 1) max = Math.max(max, levels[j]);
    return Number((max / peak).toFixed(3));
  });
}

/**
 * The message input, shaped after the pattern people already know from their
 * phone: one pill holding emoji, the draft and attachments, and a single round
 * button to its right that is the microphone while the draft is empty and the
 * send button the moment there is something to send.
 *
 * That one button is the whole point — a voice note is record, then send, with
 * no attach-then-send detour in between.
 */
export function ChatComposer({
  value,
  onChange,
  onSubmit,
  attachments,
  onAttachmentsChange,
  placeholder,
  disabled,
  inputRef,
  onOpenPoll,
  onSchedule,
  walkie,
  interceptKeyDown,
  hideAttachments,
  dropZoneRef,
  children,
  className,
}: ChatComposerProps) {
  const fallbackRef = useRef<HTMLTextAreaElement>(null);
  const textarea = inputRef ?? fallbackRef;
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  /* Read inside `recorder.onstop`, which is created before the send decision. */
  const sendOnStopRef = useRef(false);
  const objectUrlRef = useRef<string | null>(null);

  /* Recording instrumentation: levels for the waveform, a clock for the
     duration, and the browser's own speech recogniser when it has one. */
  const levelsRef = useRef<number[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const levelTimerRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const speechRef = useRef<SpeechCapture | null>(null);

  const [uploading, setUploading] = useState(false);
  /* Picked, previewed and captioned before anything is uploaded. */
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  /* Whether this browser is the one making the transcript. Only when the
     deployment has no server provider to detect the language for us — see
     `startRecording`. */
  const sttAvailable = Boolean(useAssistantStatus().data?.stt);
  const localSpeech = !sttAvailable && speechRecognitionAvailable();
  const [speechLang, setSpeechLang] = useState(dictationLanguage);

  const [failedVoice, setFailedVoice] = useState<{
    file: File;
    waveform: number[];
    durationMs: number;
    transcript?: string;
  } | null>(null);

  const hasDraft = Boolean(value.trim() || attachments.length);
  /* What is already attached counts against the limit as much as what is
     waiting in the preview — the server caps the message, not the picker. */
  const attachmentRoom = Math.max(0, ATTACHMENT_LIMIT - attachments.length);
  /* The server rejects anything longer, and it used to do so only after the
     send — a long paste came back as a validation error with no warning that
     there was a limit at all. The count appears once it is worth knowing. */
  const tooLong = value.length > MAX_MESSAGE_LENGTH;
  const nearLimit = value.length > MAX_MESSAGE_LENGTH * 0.9;
  const busy = Boolean(disabled) || uploading || tooLong;

  /* Dropping a file is the same act as picking one, so it lands in the same
     place: the preview dialog, where it is looked at and captioned before it
     goes anywhere. An edit has nowhere to put it, so it stays off there — but
     a send still in flight does not block it, since the draft it belongs to is
     already gone. */
  const dropping = useFileDrop({
    targetRef: dropZoneRef ?? rootRef,
    onFiles: queueFiles,
    enabled: !hideAttachments,
  });

  /* Grow with the draft, then scroll — the same ceiling as a phone keyboard. */
  useLayoutEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  }, [value, textarea]);

  useEffect(() => {
    if (!recording) {
      setSeconds(0);
      return;
    }
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);

  /* Escape aborts a recording, matching every other overlay in the app. */
  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopRecording(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recording]);

  useEffect(
    () => () => {
      chunksRef.current = [];
      recorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      teardownMetering();
      speechRef.current?.abort();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  function teardownMetering() {
    if (levelTimerRef.current) window.clearInterval(levelTimerRef.current);
    levelTimerRef.current = null;
    void audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
  }

  async function uploadFiles(files: File[]): Promise<MessageAttachment[]> {
    if (!files.length) return [];
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((file) => form.append('files', file));
      form.append('purpose', 'chat');
      form.append('folder', '/chat');
      const result = await api.upload<{ items: FileAsset[] }>('/files', form);
      if (!result.items.length) throw new Error('Upload returned no file');
      return result.items.map((asset) => ({
        id: asset.id,
        name: asset.name,
        url: asset.url,
        mimeType: asset.mimeType,
        size: asset.size,
        // Carried so readers on any client can shape the bubble and paint a
        // blurred stand-in before the picture itself arrives.
        ...(asset.width ? { width: asset.width } : {}),
        ...(asset.height ? { height: asset.height } : {}),
        ...(asset.blurhash ? { blurhash: asset.blurhash } : {}),
      }));
    } catch (error) {
      toast.error('Could not attach media', error instanceof Error ? error.message : undefined);
      return [];
    } finally {
      setUploading(false);
    }
  }

  async function upload(file: File): Promise<MessageAttachment | null> {
    return (await uploadFiles([file]))[0] ?? null;
  }

  /**
   * Everything picked — the button, the camera, a drop, a paste — queues here
   * and opens the preview. Nothing is uploaded until that dialog is sent, so
   * changing your mind about a file costs nothing and leaves nothing behind.
   */
  function queueFiles(files: File[]) {
    if (!files.length) return;
    const room = attachmentRoom - pendingFiles.length;
    if (room <= 0) {
      toast.error('Attachment limit reached', 'Send what you have before adding more.');
      return;
    }
    setPendingFiles((current) => [...current, ...files.slice(0, room)]);
    if (files.length > room) {
      toast.error(
        'Some files were left out',
        `A message can carry up to ${ATTACHMENT_LIMIT} files.`,
      );
    }
  }

  /** Uploads the queue and sends it as one message with the caption as body. */
  async function sendPending(caption: string): Promise<boolean> {
    const uploaded = await uploadFiles(pendingFiles);
    /* Nothing uploaded means the request failed and said so: leave the queue
       and the caption where they are so one tap can try again. */
    if (!uploaded.length) return false;
    setPendingFiles([]);
    onSubmit(uploaded, { body: caption });
    return true;
  }

  /** Pasting a screenshot or a file straight into the draft previews it. */
  function handlePaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = Array.from(event.clipboardData.files);
    if (!files.length) return;
    event.preventDefault();
    queueFiles(files);
  }

  async function sendVoiceNote(
    file: File,
    waveform: number[],
    durationMs: number,
    transcript?: string,
  ) {
    const attachment = await upload(file);
    if (!attachment) {
      /* Hold on to the recording so one tap can retry instead of re-recording. */
      setFailedVoice({ file, waveform, durationMs, transcript });
      return;
    }
    setFailedVoice(null);
    onSubmit([
      {
        ...attachment,
        kind: 'voice',
        durationMs,
        waveform: waveform.length ? waveform : undefined,
        transcript,
        transcriptStatus: transcript ? 'ready' : undefined,
        transcriptSource: transcript ? 'browser' : undefined,
      },
    ]);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, preferredRecordingType());
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      levelsRef.current = [];
      sendOnStopRef.current = false;
      startedAtRef.current = Date.now();

      /* Sample the input level on a timer and keep the peaks. Doing it here,
         while the audio is already in memory, means nothing has to decode the
         file later just to draw a waveform. */
      try {
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        analyser.fftSize = 1024;
        context.createMediaStreamSource(stream).connect(analyser);
        const buffer = new Uint8Array(analyser.frequencyBinCount);
        audioContextRef.current = context;
        levelTimerRef.current = window.setInterval(() => {
          analyser.getByteTimeDomainData(buffer);
          let sum = 0;
          for (const sample of buffer) {
            const centred = (sample - 128) / 128;
            sum += centred * centred;
          }
          const rms = Math.sqrt(sum / buffer.length);
          levelsRef.current.push(rms);
          setLevel(Math.min(1, rms * 3));
        }, LEVEL_INTERVAL);
      } catch {
        // No metering: the waveform falls back to a placeholder shape.
      }

      /* Two ways to get a transcript, and the deployment decides which.
         Where a server provider exists it wins, because it detects the
         language from the audio and the browser cannot: `SpeechRecognition`
         is handed one language and hears everything as that, so an Arabic
         note recognised as English comes back as confident words nobody
         said — silently, into the transcript everyone who cannot play the
         note will read. Without a provider that is still better than no
         transcript at all, so it runs, in the language chosen beside the
         microphone rather than the one the buttons happen to be in. */
      speechRef.current = localSpeech ? startSpeechCapture(dictationLocale()) : null;

      recorder.ondataavailable = (event) =>
        event.data.size && chunksRef.current.push(event.data);
      recorder.onstop = () => {
        const type = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const shouldSend = sendOnStopRef.current;
        const waveform = toWaveform(levelsRef.current);
        const durationMs = Date.now() - startedAtRef.current;
        chunksRef.current = [];
        levelsRef.current = [];
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        teardownMetering();
        setLevel(0);
        setRecording(false);

        const speech = speechRef.current;
        speechRef.current = null;
        if (!shouldSend || !blob.size) {
          speech?.abort();
          return;
        }

        // Extension has to track the real container, or iOS refuses the file
        // on the strength of the name alone.
        const file = new File([blob], `voice-note-${Date.now()}.${extensionFor(type)}`, {
          type,
        });
        if (!speech) {
          void sendVoiceNote(file, waveform, durationMs);
          return;
        }
        // A transcript is worth a moment's wait, but not a hung send: the
        // capture resolves with whatever it has after a short grace period.
        void speech.finish().then((transcript) => {
          void sendVoiceNote(file, waveform, durationMs, transcript || undefined);
        });
      };

      recorder.start();
      setRecording(true);
    } catch (error) {
      toast.error(
        'Microphone unavailable',
        error instanceof Error ? error.message : 'Check the browser’s microphone permission.',
      );
    }
  }

  function stopRecording(send: boolean) {
    sendOnStopRef.current = send;
    if (!send) chunksRef.current = [];
    recorderRef.current?.stop();
  }

  function insertEmoji(text: string) {
    onChange(`${value}${text}`);
    setEmojiOpen(false);
    textarea.current?.focus();
  }

  /**
   * Applies a text transform and restores the selection afterwards. Without
   * the restore the caret jumps to the end on every formatting action, which
   * makes the toolbar unusable for anything but the last word.
   */
  function applyEdit(edit: (value: string, start: number, end: number) => TextEdit) {
    const el = textarea.current;
    if (!el) return;
    const {
      value: next,
      selectionStart,
      selectionEnd,
    } = edit(value, el.selectionStart, el.selectionEnd);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(selectionStart, selectionEnd);
    });
  }

  const formatActions = {
    bold: () => applyEdit((v, s, e) => toggleWrap(v, s, e, '**')),
    italic: () => applyEdit((v, s, e) => toggleWrap(v, s, e, '_')),
    strike: () => applyEdit((v, s, e) => toggleWrap(v, s, e, '~~')),
    code: () => applyEdit((v, s, e) => toggleWrap(v, s, e, '`')),
    codeBlock: () => applyEdit(wrapCodeFence),
    spoiler: () => applyEdit((v, s, e) => toggleWrap(v, s, e, '||')),
    quote: () => applyEdit((v, s, e) => toggleLinePrefix(v, s, e, '> ')),
    bullets: () => applyEdit((v, s, e) => toggleLinePrefix(v, s, e, '- ')),
    numbers: () => applyEdit((v, s, e) => toggleLinePrefix(v, s, e, (i) => `${i + 1}. `)),
    link: () => applyEdit(insertLink),
  };

  /** The shortcuts people already have in their fingers from every editor. */
  function handleFormattingKeys(event: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
    if (!(event.metaKey || event.ctrlKey)) return false;
    const key = event.key.toLowerCase();
    const action =
      key === 'b'
        ? formatActions.bold
        : key === 'i'
          ? formatActions.italic
          : key === 'k'
            ? formatActions.link
            : key === 'e'
              ? formatActions.code
              : event.shiftKey && key === 'x'
                ? formatActions.strike
                : null;
    if (!action) return false;
    event.preventDefault();
    action();
    return true;
  }

  const iconButton =
    'grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink disabled:pointer-events-none disabled:opacity-50';

  const dropOverlay = (
    <div className="pointer-events-none absolute inset-0 z-30 grid place-items-center bg-surface-1/85 p-4 ring-2 ring-inset ring-brand-500 backdrop-blur-[1px]">
      <div className="flex flex-col items-center gap-2 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-600/15 text-brand-300">
          <AttachIcon size="lg" />
        </span>
        <p className="text-sm font-semibold text-ink">Drop to attach</p>
        <p className="text-xs text-slate-400">
          Up to {ATTACHMENT_LIMIT} files go out with your next message
        </p>
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {dropping &&
        (dropZoneRef?.current
          ? createPortal(dropOverlay, dropZoneRef.current)
          : dropOverlay)}

      {children}

      {attachments.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {attachments.map((attachment) => {
            const isImage = attachment.mimeType.startsWith('image/');
            const isAudio =
              attachment.kind === 'voice' || attachment.mimeType.startsWith('audio/');
            const Icon = isAudio ? MicIcon : fileIconFor(attachment.mimeType);
            const label =
              attachment.kind === 'gif'
                ? 'GIF'
                : attachment.kind === 'sticker'
                  ? 'Sticker'
                  : isAudio
                    ? 'Voice note'
                    : attachment.name;
            return (
              <li
                key={attachment.id}
                className="flex min-w-0 items-center gap-2 rounded-xl bg-surface-2 p-1.5 pr-1 text-xs text-slate-300 ring-1 ring-surface-3"
              >
                {isImage ? (
                  <img
                    src={attachment.url}
                    alt=""
                    className="h-9 w-9 rounded-lg object-cover"
                  />
                ) : (
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-surface-3 text-brand-300">
                    <Icon />
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block max-w-36 truncate">{label}</span>
                  {attachment.size > 0 && (
                    <span className="block text-[11px] text-slate-500">
                      {formatBytes(attachment.size)}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${label}`}
                  onClick={() =>
                    onAttachmentsChange(attachments.filter((item) => item.id !== attachment.id))
                  }
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
                >
                  <CloseIcon size="sm" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {failedVoice && (
        <div className="mb-2 flex items-center gap-2 rounded-xl bg-red-500/10 px-3 py-2 text-xs ring-1 ring-red-500/30">
          <span className="min-w-0 flex-1 text-red-300">
            Voice note didn’t send. It’s still here.
          </span>
          <button
            type="button"
            className="font-medium text-brand-300 transition-colors hover:text-brand-200"
            onClick={() =>
              void sendVoiceNote(
                failedVoice.file,
                failedVoice.waveform,
                failedVoice.durationMs,
                failedVoice.transcript,
              )
            }
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => setFailedVoice(null)}
            aria-label="Discard voice note"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
          >
            <TrashIcon size="sm" />
          </button>
        </div>
      )}

      {nearLimit && (
        <p
          aria-live="polite"
          className={cn(
            'mb-1 text-right text-[11px] tabular-nums',
            tooLong ? 'font-medium text-red-300' : 'text-slate-500',
          )}
        >
          {tooLong
            ? `${(value.length - MAX_MESSAGE_LENGTH).toLocaleString()} characters over the limit`
            : `${value.length.toLocaleString()} / ${MAX_MESSAGE_LENGTH.toLocaleString()}`}
        </p>
      )}

      {formatOpen && !recording && <FormatToolbar actions={formatActions} />}

      <div className="flex items-end gap-2">
        {recording ? (
          /* Recording takes over the pill: one way in, two ways out. */
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-3xl bg-red-500/10 py-1.5 pl-1.5 pr-4 ring-1 ring-red-500/30">
            <button
              type="button"
              onClick={() => stopRecording(false)}
              aria-label="Discard recording"
              title="Discard recording (Esc)"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-red-100 transition-colors hover:bg-red-500/20 hover:text-white"
            >
              <TrashIcon />
            </button>
            <span className="relative grid h-2.5 w-2.5 shrink-0 place-items-center">
              <span className="absolute inset-0 animate-pulse-ring rounded-full bg-red-400" />
              <span className="h-2 w-2 rounded-full bg-red-400" />
            </span>
            <span className="shrink-0 text-sm font-medium tabular-nums text-red-200">
              {formatDuration(seconds)}
            </span>
            {/* The meter is the real input level, not an animation — you can
                see whether the microphone is actually picking you up. */}
            <span aria-hidden className="flex h-6 min-w-0 flex-1 items-center gap-[3px]">
              {Array.from({ length: 16 }, (_, i) => {
                const height = Math.max(
                  3,
                  Math.min(1, level * (0.6 + Math.sin((i / 16) * Math.PI) * 0.8)) * 22,
                );
                return (
                  <span
                    key={i}
                    className="w-[3px] rounded-full bg-red-400/80 transition-[height] duration-100"
                    style={{ height: `${height}px` }}
                  />
                );
              })}
            </span>
            <span className="sr-only" role="status">
              Recording, {seconds} seconds
            </span>
          </div>
        ) : (
          <>
            <div className={cn('relative shrink-0', hideAttachments && 'hidden')}>
              <button
                type="button"
                aria-label="Add to message"
                title="Add to message"
                aria-haspopup="menu"
                aria-expanded={actionsOpen}
                onClick={() => {
                  setEmojiOpen(false);
                  setGifOpen(false);
                  setActionsOpen((open) => !open);
                }}
                disabled={uploading}
                className={cn(
                  'grid h-11 w-11 place-items-center rounded-full text-slate-400 transition-[background-color,color,transform] duration-150 hover:bg-surface-3 hover:text-ink active:scale-95 disabled:pointer-events-none',
                  actionsOpen && 'bg-surface-3 text-ink',
                )}
              >
                {uploading ? <Spinner /> : <PlusIcon />}
              </button>

              {actionsOpen && (
                <div
                  role="menu"
                  aria-label="Add to message"
                  className="absolute bottom-full left-0 z-30 mb-2 w-52 overflow-hidden rounded-2xl border border-surface-4 bg-surface-2 p-1.5 shadow-xl shadow-black/25"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-surface-3 hover:text-ink"
                    onClick={() => {
                      setActionsOpen(false);
                      fileRef.current?.click();
                    }}
                  >
                    <AttachIcon />
                    Attach a file
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-surface-3 hover:text-ink"
                    onClick={() => {
                      setActionsOpen(false);
                      cameraRef.current?.click();
                    }}
                  >
                    <CameraIcon />
                    Take photo or video
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-surface-3 hover:text-ink"
                    onClick={() => {
                      setActionsOpen(false);
                      setGifOpen(true);
                    }}
                  >
                    <span aria-hidden className="w-5 text-center text-[11px] font-bold">
                      GIF
                    </span>
                    GIF or sticker
                  </button>
                  {onOpenPoll && (
                    <button
                      type="button"
                      role="menuitem"
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-surface-3 hover:text-ink"
                      onClick={() => {
                        setActionsOpen(false);
                        onOpenPoll();
                      }}
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                        <path
                          d="M5 20V10m7 10V4m7 16v-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      Create a poll
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-surface-3 hover:text-ink"
                    onClick={() => {
                      setActionsOpen(false);
                      setFormatOpen(true);
                      textarea.current?.focus();
                    }}
                  >
                    <span aria-hidden className="w-5 text-center text-sm font-semibold">
                      Aa
                    </span>
                    Format message
                  </button>
                </div>
              )}
            </div>

            <div className="flex min-w-0 flex-1 items-end gap-0.5 rounded-3xl border border-surface-4 bg-surface-2 p-1 transition-colors focus-within:border-brand-500">
              <button
                type="button"
                aria-label="Add an emoji"
                title="Add an emoji"
                aria-expanded={emojiOpen}
                className={iconButton}
                onClick={() => {
                  setActionsOpen(false);
                  setGifOpen(false);
                  setEmojiOpen((open) => !open);
                }}
              >
                <EmojiIcon />
              </button>

              <textarea
                ref={textarea}
                rows={1}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={(e) => {
                  // The chat gets first refusal: while a mention list is open
                  // its arrows and Enter belong to that, not to the box.
                  if (interceptKeyDown?.(e)) {
                    e.preventDefault();
                    return;
                  }
                  if (handleFormattingKeys(e)) return;
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit();
                  }
                }}
                placeholder={placeholder}
                aria-label={placeholder}
                /* 16px on touch stops iOS Safari zooming the page on focus. */
                className="min-w-0 flex-1 resize-none self-center bg-transparent px-1 py-2 text-base leading-6 text-slate-100 outline-none placeholder:text-slate-500 disabled:opacity-60 sm:text-[0.9375rem]"
              />

              <button
                type="button"
                aria-label={formatOpen ? 'Hide formatting' : 'Show formatting'}
                title="Formatting"
                aria-pressed={formatOpen}
                className={cn(iconButton, formatOpen && 'ink-brand bg-surface-3')}
                onClick={() => setFormatOpen((open) => !open)}
              >
                <span aria-hidden className="text-sm font-semibold">
                  Aa
                </span>
              </button>

              {/* GIF, poll and attach used to sit here as well as behind the
                  + menu, which put six controls round one text field. They
                  live in the menu only; emoji and formatting stay, because
                  they act on what is being typed. */}
              {/* Any file type: the transcript already renders documents, audio
                and archives with their own icons, and the server only restricts
                uploads for avatars. */}
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  queueFiles(Array.from(event.target.files ?? []));
                  event.target.value = '';
                }}
              />
              <input
                ref={cameraRef}
                type="file"
                accept="image/*,video/*"
                capture="environment"
                hidden
                onChange={(event) => {
                  queueFiles(Array.from(event.target.files ?? []));
                  event.target.value = '';
                }}
              />
            </div>
          </>
        )}

        {/* Send later only makes sense once there is something to send. */}
        {onSchedule && hasDraft && !recording && (
          <div className="relative">
            <button
              type="button"
              aria-label="Send later"
              title="Send later"
              aria-expanded={scheduleOpen}
              onClick={() => setScheduleOpen((open) => !open)}
              className="grid h-11 w-9 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="1.8" />
                <path
                  d="M12 9.5V13l2.5 1.5M9 2h6"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {scheduleOpen && (
              <ScheduleSendMenu
                className="absolute bottom-full right-0 z-20 mb-2"
                onClose={() => setScheduleOpen(false)}
                onSchedule={(sendAt) => {
                  setScheduleOpen(false);
                  onSchedule(sendAt);
                }}
              />
            )}
          </div>
        )}

        {/**
         * One button, two jobs: microphone while there is nothing to send,
         * send the moment there is — including the moment a recording ends.
         */}
        {/* Editing has no voice-note branch: an emptied edit box is an
            unfinished edit, not an invitation to record over it. */}
        {(() => {
          const sends = hasDraft || recording || hideAttachments;
          const label = recording
            ? 'Send voice note'
            : hideAttachments
              ? 'Save changes'
              : hasDraft
                ? 'Send message'
                : 'Record a voice note';
          return (
            <>
            {/* Which language the browser will listen for. Only shown where
                the browser is doing the recognising and only when a voice
                note is what the button would start, so it never appears as an
                unexplained control beside a half-typed message. */}
            {localSpeech && !sends && !recording && (
              <button
                type="button"
                onClick={() => {
                  const next = speechLang === 'ar' ? 'en' : 'ar';
                  setDictationLanguage(next);
                  setSpeechLang(next);
                }}
                aria-label={`Dictation language: ${speechLang === 'ar' ? 'Arabic' : 'English'}. Tap to switch.`}
                title={`Voice notes are transcribed as ${speechLang === 'ar' ? 'Arabic' : 'English'}`}
                className="h-11 shrink-0 rounded-full px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
              >
                {speechLang}
              </button>
            )}
            {/* Beside the microphone, not instead of it: a voice note is
                something you send, the radio is something you interrupt with,
                and swapping one for the other under the thumb would send the
                wrong one. Hidden while recording, which owns the mic. */}
            {walkie && !recording && (
              <WalkieButton
                target={{ channelId: walkie.channelId }}
                targetName={walkie.channelName}
                disabled={busy}
              />
            )}
            <button
              type="button"
              aria-label={label}
              title={label}
              disabled={busy || (hideAttachments && !hasDraft)}
              onClick={() => {
                if (recording) stopRecording(true);
                else if (hasDraft || hideAttachments) onSubmit();
                else void startRecording();
              }}
              className={cn(
                'grid h-11 w-11 shrink-0 place-items-center rounded-full transition-[background-color,transform] duration-150 active:scale-95 disabled:pointer-events-none disabled:opacity-50',
                sends
                  ? 'bg-brand-600 text-white hover:bg-brand-500'
                  : 'text-slate-400 hover:bg-surface-3 hover:text-ink',
              )}
            >
              {sends ? <SendIcon /> : <MicIcon />}
            </button>
            </>
          );
        })()}
      </div>

      {emojiOpen && (
        <EmojiPicker
          className="absolute bottom-full left-0 z-20 mb-2"
          onClose={() => setEmojiOpen(false)}
          onPick={(pick) => insertEmoji(pick.value)}
        />
      )}

      {/* A picked file is looked at, captioned and sent from here — the
          composer never holds it as a chip you cannot see inside. */}
      {pendingFiles.length > 0 && (
        <AttachmentPreview
          files={pendingFiles}
          room={attachmentRoom - pendingFiles.length}
          initialCaption={value}
          onFilesChange={setPendingFiles}
          onCancel={() => setPendingFiles([])}
          onSend={sendPending}
        />
      )}

      {gifOpen && (
        <GifPicker
          className="absolute bottom-full left-0 z-20 mb-2"
          onClose={() => setGifOpen(false)}
          onPick={(attachment) => {
            setGifOpen(false);
            // A GIF is the message: sending it straight through matches what
            // every other chat app does, and avoids a pointless second tap.
            onSubmit([attachment]);
          }}
        />
      )}
    </div>
  );
}
